import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTenantPath } from '../lib/tenantPath';
import { Users, Cube, FileText, CheckCircle, TrendUp, Clock, ChartLineUp, ChartPie, UsersThree, CurrencyInr, Target, Funnel, XCircle, Receipt, Package, DownloadSimple } from '@phosphor-icons/react';
import { exportRowsToCsv } from '../lib/csvExport';
import useAuthStore from '../store/authStore';
import { useGmTransport } from '../lib/gmTransport';
import GmTransportToggle from '../components/GmTransportToggle';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, Legend, ComposedChart
} from 'recharts';
import MyGoalsWidget from '../components/MyGoalsWidget';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#00a69c', '#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6', '#EC4899', '#10B981', '#F97316'];

const formatCurrency = (val) => `₹${Number(val || 0).toLocaleString('en-IN')}`;
const formatMonth = (m) => {
  if (!m) return '';
  const [y, mo] = m.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(mo) - 1]} ${y?.slice(2)}`;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const tp = useTenantPath();
  const { user } = useAuthStore();
  const [stats, setStats] = useState(null);
  const [sales, setSales] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [customerInsights, setCustomerInsights] = useState(null);
  const [lordAnalytics, setLordAnalytics] = useState(null);
  const [productInsights, setProductInsights] = useState(null);
  const [piConv, setPiConv] = useState(null);
  // iter132.4 — Customer terms templates usage tile (top reused combos).
  const [tplUsage, setTplUsage] = useState(null);
  const [forecastAccuracy, setForecastAccuracy] = useState(null);
  const [gmSummary, setGmSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const canSeeGm = ['Vice President', 'President'].includes(user?.role);
  const { withTransport } = useGmTransport();
  // Feb 2026 — Net (pre-GST) vs Gross toggle. Corporate sales targets exclude
  // GST, so default is NET. Persisted in localStorage per-user (per-browser)
  // so managers don't have to re-toggle on every page load.
  const [includeGst, setIncludeGst] = useState(() => {
    try { return localStorage.getItem('vooxy.dashboardIncludeGst') === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('vooxy.dashboardIncludeGst', includeGst ? '1' : '0'); } catch { /* noop */ }
  }, [includeGst]);

  // Timeline filter (KPI strip only) — { id, label, start_date, end_date }
  const buildRange = (id) => {
    const today = new Date();
    const isoDay = (d) => d.toISOString().slice(0, 10);
    const startOfDay = (d) => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
    const subDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() - n + 1); return startOfDay(x); };
    const subMonths = (d, n) => { const x = new Date(d); x.setMonth(x.getMonth() - n); x.setDate(x.getDate() + 1); return startOfDay(x); };
    const fyStart = () => {
      // Indian FY: Apr 1 — Mar 31
      const y = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
      return new Date(y, 3, 1);
    };
    if (id === 'all') return { start: '', end: '' };
    if (id === '1d') return { start: isoDay(startOfDay(today)), end: isoDay(today) };
    if (id === '7d') return { start: isoDay(subDays(today, 7)), end: isoDay(today) };
    if (id === '30d') return { start: isoDay(subDays(today, 30)), end: isoDay(today) };
    if (id === '3m') return { start: isoDay(subMonths(today, 3)), end: isoDay(today) };
    if (id === '6m') return { start: isoDay(subMonths(today, 6)), end: isoDay(today) };
    if (id === 'fytd') return { start: isoDay(fyStart()), end: isoDay(today) };
    return { start: '', end: '' };
  };
  const [rangeId, setRangeId] = useState('all'); // '1d' | '7d' | '30d' | '3m' | '6m' | 'fytd' | 'custom' | 'all'
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Resolve the active date window
  const activeRange = rangeId === 'custom'
    ? { start: customStart, end: customEnd }
    : buildRange(rangeId);

  // _user kept for future role checks
  void user;

  const reloadStats = (start, end) => {
    const params = { include_gst: includeGst };
    if (start) params.start_date = start;
    if (end) params.end_date = end;
    return axios.get(`${API}/dashboard/stats`, { params }).then(r => setStats(r.data));
  };

  useEffect(() => {
    const gstParam = { params: { include_gst: includeGst } };
    const requests = [
      reloadStats(activeRange.start, activeRange.end),
      axios.get(`${API}/analytics/sales`, gstParam).then(r => setSales(r.data)),
      axios.get(`${API}/analytics/revenue-forecast`, gstParam).then(r => setForecast(r.data)),
      axios.get(`${API}/analytics/customers`, gstParam).then(r => setCustomerInsights(r.data)),
      axios.get(`${API}/analytics/lord`).then(r => setLordAnalytics(r.data)),
      axios.get(`${API}/analytics/pi-conversion`, gstParam).then(r => setPiConv(r.data)),
      // iter132.4 — Top customers where saved terms templates are saving VP approvals.
      axios.get(`${API}/analytics/customer-templates-usage`, { params: { limit: 5 } })
        .then(r => setTplUsage(r.data)).catch(() => setTplUsage(null)),
      // Feb 2026 — Forecast accuracy tile (personal for SE, side-by-side for managers).
      axios.get(`${API}/analytics/forecast-accuracy`).then(r => setForecastAccuracy(r.data)).catch(() => setForecastAccuracy(null)),
    ];
    Promise.all(requests).catch(console.error)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeGst]);

  // GM-dependent data refetches when the transport switch flips.
  useEffect(() => {
    axios.get(`${API}/analytics/product-insights`, { params: { include_gst: includeGst, include_transport: withTransport } })
      .then(r => setProductInsights(r.data)).catch(() => setProductInsights(null));
    if (canSeeGm) {
      axios.get(`${API}/analytics/gross-margin`, { params: { include_transport: withTransport } })
        .then(r => setGmSummary(r.data)).catch(() => setGmSummary(null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeGst, withTransport]);

  // Refetch only the KPI stats when the timeline filter changes
  useEffect(() => {
    if (loading) return undefined;
    if (rangeId === 'custom' && (!customStart || !customEnd)) return undefined;
    reloadStats(activeRange.start, activeRange.end).catch(console.error);
  }, [rangeId, customStart, customEnd]);

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-[#00a69c]">Loading analytics...</div></div>;
  }

  const tabs = [
    { id: 'overview', label: 'Sales Analytics', icon: ChartLineUp },
    { id: 'forecast', label: 'Revenue Forecast', icon: Target },
    { id: 'customers', label: 'Customer Insights', icon: UsersThree },
    { id: 'products', label: 'Product Insights', icon: Package },
    { id: 'lord', label: 'Lost Order Analysis', icon: XCircle },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="dashboard">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="app-page-title" style={{ fontFamily: 'Outfit' }} data-testid="dashboard-title">
            Analytics Dashboard
          </h1>
          <p className="text-[#6D8E87]">
            Business intelligence and performance metrics
            {' · '}
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                includeGst ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
              }`}
              data-testid="dashboard-gst-mode-badge"
            >
              {includeGst ? 'Values incl. GST (Gross)' : 'Values excl. GST (Net)'}
            </span>
          </p>
        </div>

        {/* Right-side controls: GST toggle + Timeline filter */}
        <div className="flex flex-wrap items-center gap-3">
          {canSeeGm && <GmTransportToggle testid="dashboard-gm-transport-toggle" />}
          {/* Feb 2026 — Net (default) / Gross toggle. Persisted per-browser. */}
          <label
            className="inline-flex items-center gap-2 cursor-pointer select-none px-3 py-2 rounded-full border border-[#D5E8E4] bg-white hover:border-[#00a69c] transition"
            data-testid="gst-toggle-wrapper"
            title="Corporate sales targets exclude GST. Turn on to see gross figures (with GST) instead."
          >
            <span className="text-[11px] font-semibold text-[#3B5A54]">Include GST</span>
            <span className="relative inline-block w-9 h-5">
              <input
                type="checkbox"
                checked={includeGst}
                onChange={e => setIncludeGst(e.target.checked)}
                className="sr-only peer"
                data-testid="gst-toggle-input"
              />
              <span className="absolute inset-0 rounded-full bg-slate-300 peer-checked:bg-[#00a69c] transition" />
              <span className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-transform peer-checked:translate-x-4" />
            </span>
          </label>

          {/* Timeline filter — affects KPI strip only */}
          <div className="flex flex-wrap items-center gap-2" data-testid="timeline-filter">
          {[
            { id: 'all', label: 'All' },
            { id: '1d', label: '1D' },
            { id: '7d', label: '7D' },
            { id: '30d', label: '30D' },
            { id: '3m', label: '3M' },
            { id: '6m', label: '6M' },
            { id: 'fytd', label: 'FYTD' },
            { id: 'custom', label: 'Custom' },
          ].map(opt => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setRangeId(opt.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                rangeId === opt.id
                  ? 'bg-[#00a69c] text-white border-[#00a69c]'
                  : 'bg-white text-[#3B5A54] border-[#D5E8E4] hover:bg-[#F4F9F7]'
              }`}
              data-testid={`timeline-${opt.id}`}
            >
              {opt.label}
            </button>
          ))}
          {rangeId === 'custom' && (
            <div className="flex items-center gap-2 ml-1" data-testid="timeline-custom-range">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="px-2 py-1 border border-[#D5E8E4] rounded-md text-xs text-[#3B5A54] focus:outline-none focus:ring-2 focus:ring-[#00a69c]/30"
                data-testid="timeline-custom-start"
              />
              <span className="text-xs text-[#6D8E87]">→</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="px-2 py-1 border border-[#D5E8E4] rounded-md text-xs text-[#3B5A54] focus:outline-none focus:ring-2 focus:ring-[#00a69c]/30"
                data-testid="timeline-custom-end"
              />
            </div>
          )}
        </div>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4 mb-6" data-testid="stats-grid">
        {[
          { label: 'Revenue (Won)', value: formatCurrency(stats?.total_revenue), color: '#00a69c', bg: '#E2F3F0', icon: CurrencyInr, route: '/leads', tooltip: 'Booked revenue — sum of winning-quote values from Won leads only. Approved-but-not-Won quotes are excluded (see Pipeline Forecast tab for that view).', testid: 'kpi-revenue-won' },
          { label: 'Approved Pipeline', value: formatCurrency(stats?.approved_pipeline_value), color: '#0891B2', bg: '#CFFAFE', icon: TrendUp, route: '/quotations?status=approved', tooltip: 'Committed-but-not-closed value — sum of every approved quote whose lead is still open. Flips into Revenue (Won) once the lead is marked Won.', testid: 'kpi-approved-pipeline' },
          { label: 'Customers', value: stats?.total_customers || 0, color: '#8B5CF6', bg: '#EDE9FE', icon: UsersThree, route: '/customers' },
          { label: 'Leads', value: stats?.total_leads || 0, color: '#3B82F6', bg: '#DBEAFE', icon: Users, route: '/leads' },
          { label: 'Quotations', value: stats?.total_quotations || 0, color: '#F59E0B', bg: '#FEF3C7', icon: FileText, route: '/quotations' },
          { label: 'Conversion', value: `${stats?.conversion_rate ?? sales?.conversion_rate ?? 0}%`, color: '#10B981', bg: '#D1FAE5', icon: TrendUp, route: null },
          { label: 'Pending', value: stats?.pending_approvals || 0, color: '#EF4444', bg: '#FEE2E2', icon: Clock, route: '/approvals' },
          ...(canSeeGm ? [{
            label: 'Gross Margin',
            value: gmSummary ? `${gmSummary.gm_pct}%` : '—',
            color: '#F59E0B',
            bg: '#FEF3C7',
            icon: ChartPie,
            route: '/gross-margin',
            testid: 'kpi-gross-margin',
          }] : []),
        ].map((kpi, i) => (
          <button
            key={i}
            type="button"
            onClick={() => kpi.route && navigate(kpi.route)}
            disabled={!kpi.route}
            className={`medical-card p-4 text-left ${kpi.route ? 'cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all' : 'cursor-default'}`}
            data-testid={kpi.testid || `kpi-${kpi.label.toLowerCase()}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: kpi.bg, color: kpi.color }}>
                <kpi.icon size={16} weight="bold" />
              </div>
              <span className="text-xs text-[#6D8E87]">{kpi.label}</span>
            </div>
            <div className="text-xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>{kpi.value}</div>
          </button>
        ))}
      </div>

      {/* Feb 2026 — Forecast Accuracy Tile */}
      {forecastAccuracy && ((forecastAccuracy.you?.count || 0) > 0 || (forecastAccuracy.team?.count || 0) > 0) && (
        <div className="medical-card p-5 mb-6" data-testid="forecast-accuracy-tile">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                <Target size={22} weight="bold" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>Forecast Accuracy</h3>
                <p className="text-xs text-[#6D8E87]">
                  How close SE forecasts came to actual won value · last {forecastAccuracy.window_days || 90} days
                </p>
              </div>
            </div>
          </div>

          <div className={`grid ${forecastAccuracy.is_manager && forecastAccuracy.team ? 'md:grid-cols-2' : 'md:grid-cols-1'} gap-4`}>
            {['you', 'team'].map((scope) => {
              const s = forecastAccuracy[scope];
              if (!s) return null;
              if (scope === 'team' && !forecastAccuracy.is_manager) return null;
              const label = scope === 'you' ? 'You' : 'Your Team';
              const biasLabel = s.bias_pct > 0.5 ? 'over-forecast' : s.bias_pct < -0.5 ? 'under-forecast' : 'neutral';
              const biasColor = Math.abs(s.bias_pct) < 10 ? 'text-emerald-600' : Math.abs(s.bias_pct) < 25 ? 'text-amber-600' : 'text-rose-600';
              return (
                <div key={scope} className="p-4 rounded-lg bg-[#F4F9F7] border border-[#E2ECEA]" data-testid={`forecast-accuracy-${scope}`}>
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-bold text-[#3B5A54] uppercase tracking-wide">{label}</p>
                    <p className="text-[10px] text-[#6D8E87]">{s.count} closed lead{s.count === 1 ? '' : 's'}</p>
                  </div>
                  {s.count === 0 ? (
                    <p className="text-xs text-[#6D8E87] py-4 text-center">No closed leads with recorded forecasts yet.</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <div className="text-[10px] text-[#6D8E87]">On-target</div>
                          <div className="text-xl font-bold text-emerald-700" style={{ fontFamily: 'Outfit' }} data-testid={`fa-${scope}-ontarget`}>{s.on_target_pct}%</div>
                          <div className="text-[9px] text-[#6D8E87]">within ±10%</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-[#6D8E87]">Avg. |error|</div>
                          <div className="text-xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }} data-testid={`fa-${scope}-avgabs`}>{s.avg_abs_pct_off}%</div>
                          <div className="text-[9px] text-[#6D8E87]">off from actual</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-[#6D8E87]">Bias</div>
                          <div className={`text-xl font-bold ${biasColor}`} style={{ fontFamily: 'Outfit' }} data-testid={`fa-${scope}-bias`}>
                            {s.bias_pct > 0 ? '+' : ''}{s.bias_pct}%
                          </div>
                          <div className="text-[9px] text-[#6D8E87]">{biasLabel}</div>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-[#E2ECEA] text-[10px] text-[#6D8E87] flex justify-between">
                        <span>Forecast: <span className="font-semibold text-[#3B5A54]">{formatCurrency(s.sum_initial)}</span></span>
                        <span>Actual: <span className="font-semibold text-[#00a69c]">{formatCurrency(s.sum_actual)}</span></span>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* PI Conversion Widget */}
      {piConv && piConv.total > 0 && (
        <div className="medical-card p-5 mb-6" data-testid="pi-conversion-widget">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#E2F3F0] text-[#00a69c] flex items-center justify-center">
                <Receipt size={22} weight="bold" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>PI Conversion Rate</h3>
                <p className="text-xs text-[#6D8E87]">How many approved Proforma Invoices convert into actual Won orders</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate(tp('/proforma-invoices'))}
              className="text-xs font-semibold text-[#00a69c] hover:underline"
              data-testid="pi-widget-view-all"
            >
              View all PIs →
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <div className="p-3 rounded-lg bg-[#F4F9F7]" data-testid="pi-stat-rate">
              <div className="text-xs text-[#6D8E87] mb-1">Conversion Rate</div>
              <div className="text-2xl font-bold text-[#00a69c]" style={{ fontFamily: 'Outfit' }}>
                {piConv.conversion_rate}%
              </div>
              <div className="text-[10px] text-[#6D8E87] mt-1">{piConv.converted} of {piConv.approved} approved</div>
            </div>
            <div className="p-3 rounded-lg bg-amber-50">
              <div className="text-xs text-[#6D8E87] mb-1">Pending Approval</div>
              <div className="text-2xl font-bold text-amber-700" style={{ fontFamily: 'Outfit' }}>{piConv.pending}</div>
            </div>
            <div className="p-3 rounded-lg bg-blue-50">
              <div className="text-xs text-[#6D8E87] mb-1">Open Pipeline</div>
              <div className="text-2xl font-bold text-blue-700" style={{ fontFamily: 'Outfit' }}>{piConv.approved_outstanding}</div>
              <div className="text-[10px] text-[#6D8E87] mt-1">{formatCurrency(piConv.open_pipeline_value)}</div>
            </div>
            <div className="p-3 rounded-lg bg-emerald-50">
              <div className="text-xs text-[#6D8E87] mb-1">Converted</div>
              <div className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'Outfit' }}>{piConv.converted}</div>
              <div className="text-[10px] text-[#6D8E87] mt-1">{formatCurrency(piConv.converted_value)}</div>
            </div>
            <div className="p-3 rounded-lg bg-rose-50">
              <div className="text-xs text-[#6D8E87] mb-1">Rejected</div>
              <div className="text-2xl font-bold text-rose-700" style={{ fontFamily: 'Outfit' }}>{piConv.rejected}</div>
            </div>
          </div>
        </div>
      )}

      {/* My Goals Widget (auto-hides if user has no goals) */}
      <MyGoalsWidget />

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b border-[#D5E8E4] pb-1" data-testid="analytics-tabs">
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-t-lg text-sm font-medium transition ${
              activeTab === tab.id ? 'bg-[#00a69c] text-white' : 'text-[#6D8E87] hover:bg-[#F4F9F7]'
            }`} data-testid={`tab-${tab.id}`}>
            <tab.icon size={18} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* ========== SALES ANALYTICS ========== */}
      {activeTab === 'overview' && sales && (
        <div className="space-y-6" data-testid="sales-analytics">
          {/* Revenue Trend + Avg Deal */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 medical-card p-6" data-testid="revenue-trend-chart">
              <h3 className="text-lg font-semibold text-[#052A23] mb-1" style={{ fontFamily: 'Outfit' }}>Revenue Trend</h3>
              <p className="text-xs text-[#6D8E87] mb-4">Monthly approved revenue vs total quoted</p>
              <ResponsiveContainer width="100%" height={280}>
                <ComposedChart data={sales.revenue_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2F3F0" />
                  <XAxis dataKey="month" tickFormatter={formatMonth} stroke="#6D8E87" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#6D8E87" tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={formatMonth} contentStyle={{ border: '1px solid #D5E8E4', borderRadius: '8px' }} />
                  <Legend />
                  <Area type="monotone" dataKey="total_quoted" fill="#E2F3F0" stroke="#B5D9D2" name="Total Quoted" />
                  <Bar dataKey="revenue" fill="#00a69c" radius={[4, 4, 0, 0]} name="Approved Revenue" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            <div className="space-y-4">
              <div className="medical-card p-5" data-testid="avg-deal-card">
                <p className="text-xs text-[#6D8E87] mb-1">Average Deal Size</p>
                <div className="text-2xl font-bold text-[#00a69c]" style={{ fontFamily: 'Outfit' }}>{formatCurrency(sales.avg_deal_size)}</div>
              </div>
              <div className="medical-card p-5" data-testid="conversion-card">
                <p className="text-xs text-[#6D8E87] mb-1">Conversion Rate</p>
                <div className="text-2xl font-bold text-[#00a69c]" style={{ fontFamily: 'Outfit' }}>{sales.conversion_rate}%</div>
                <div className="w-full bg-[#E2F3F0] rounded-full h-2.5 mt-3">
                  <div className="bg-[#00a69c] h-2.5 rounded-full transition-all" style={{ width: `${Math.min(sales.conversion_rate, 100)}%` }}></div>
                </div>
                <div className="flex justify-between text-xs text-[#6D8E87] mt-1">
                  <span>{sales.approved_quotations} approved</span>
                  <span>{sales.total_quotations} total</span>
                </div>
              </div>
              <div className="medical-card p-5" data-testid="status-donut">
                <p className="text-xs text-[#6D8E87] mb-2">Quote Status Split</p>
                <ResponsiveContainer width="100%" height={120}>
                  <PieChart>
                    <Pie data={sales.status_breakdown} innerRadius={30} outerRadius={50} paddingAngle={3} dataKey="value">
                      {sales.status_breakdown.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v, n) => [v, n]} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 text-xs">
                  {sales.status_breakdown.map((s, i) => (
                    <span key={i} className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }}></span>
                      {s.name} ({s.value})
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Top Reps */}
          <div className="medical-card p-6" data-testid="top-reps">
            <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>Top Sales Representatives</h3>
            {sales.top_reps.length > 0 ? (
              <div className="space-y-3">
                {sales.top_reps.map((rep, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <span className="w-7 h-7 rounded-full bg-[#E2F3F0] text-[#00a69c] flex items-center justify-center text-xs font-bold">{i + 1}</span>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-[#052A23]">{rep.name}</span>
                        <span className="text-sm font-bold text-[#00a69c]">{formatCurrency(rep.revenue)}</span>
                      </div>
                      <div className="w-full bg-[#E2F3F0] rounded-full h-1.5 mt-1">
                        <div className="bg-[#00a69c] h-1.5 rounded-full" style={{ width: `${Math.min((rep.revenue / (sales.top_reps[0]?.revenue || 1)) * 100, 100)}%` }}></div>
                      </div>
                      <span className="text-xs text-[#6D8E87]">{rep.deals} deals</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#6D8E87]">No approved deals yet to rank representatives.</p>
            )}
          </div>
        </div>
      )}

      {/* ========== REVENUE FORECAST ========== */}
      {activeTab === 'forecast' && forecast && (
        <div className="space-y-6" data-testid="revenue-forecast">
          {/* Pipeline Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Draft Pipeline', value: forecast.pipeline_draft, color: '#6D8E87', desc: '20% probability' },
              { label: 'Pending Approval', value: forecast.pipeline_pending, color: '#F59E0B', desc: '60% probability' },
              { label: 'Approved Revenue', value: forecast.pipeline_approved, color: '#00a69c', desc: '100% confirmed' },
              { label: 'Expected Revenue', value: forecast.expected_revenue, color: '#3B82F6', desc: 'Weighted forecast' },
            ].map((p, i) => (
              <div key={i} className="medical-card p-5" data-testid={`pipeline-${i}`}>
                <p className="text-xs text-[#6D8E87] mb-1">{p.label}</p>
                <div className="text-xl font-bold" style={{ color: p.color, fontFamily: 'Outfit' }}>{formatCurrency(p.value)}</div>
                <p className="text-xs text-[#6D8E87] mt-1">{p.desc}</p>
              </div>
            ))}
          </div>

          {/* Actual vs Pipeline Chart */}
          <div className="medical-card p-6" data-testid="forecast-chart">
            <h3 className="text-lg font-semibold text-[#052A23] mb-1" style={{ fontFamily: 'Outfit' }}>Actual vs Pipeline</h3>
            <p className="text-xs text-[#6D8E87] mb-4">Monthly confirmed revenue vs in-pipeline value</p>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={forecast.forecast_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2F3F0" />
                <XAxis dataKey="month" tickFormatter={formatMonth} stroke="#6D8E87" tick={{ fontSize: 11 }} />
                <YAxis stroke="#6D8E87" tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={formatMonth} contentStyle={{ border: '1px solid #D5E8E4', borderRadius: '8px' }} />
                <Legend />
                <Area type="monotone" dataKey="actual" fill="#00a69c" fillOpacity={0.3} stroke="#00a69c" strokeWidth={2} name="Confirmed Revenue" />
                <Area type="monotone" dataKey="pipeline" fill="#F59E0B" fillOpacity={0.15} stroke="#F59E0B" strokeWidth={2} name="In Pipeline" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Lead Pipeline */}
            <div className="medical-card p-6" data-testid="lead-pipeline">
              <h3 className="text-lg font-semibold text-[#052A23] mb-1" style={{ fontFamily: 'Outfit' }}>Lead Value Pipeline</h3>
              <p className="text-xs text-[#6D8E87] mb-3">Total lead value: {formatCurrency(forecast.total_lead_value)}</p>
              {forecast.lead_pipeline.length > 0 ? (
                <div className="space-y-3">
                  {forecast.lead_pipeline.map((lp) => (
                    <div key={lp.status} className="flex items-center justify-between p-3 bg-[#F4F9F7] rounded-lg">
                      <div>
                        <span className="text-sm font-medium text-[#052A23] capitalize">{lp.status}</span>
                        <span className="text-xs text-[#6D8E87] ml-2">({lp.count} leads)</span>
                      </div>
                      <span className="text-sm font-bold text-[#00a69c]">{formatCurrency(lp.value)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#6D8E87]">No lead data available.</p>
              )}
            </div>

            {/* Quotation Aging */}
            <div className="medical-card p-6" data-testid="aging-chart">
              <h3 className="text-lg font-semibold text-[#052A23] mb-1" style={{ fontFamily: 'Outfit' }}>Pending Quotation Aging</h3>
              <p className="text-xs text-[#6D8E87] mb-3">How long quotes have been waiting for action</p>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={forecast.aging_data} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2F3F0" />
                  <XAxis type="number" stroke="#6D8E87" tick={{ fontSize: 11 }} />
                  <YAxis dataKey="bucket" type="category" stroke="#6D8E87" tick={{ fontSize: 11 }} width={80} />
                  <Tooltip contentStyle={{ border: '1px solid #D5E8E4', borderRadius: '8px' }} />
                  <Bar dataKey="count" fill="#F59E0B" radius={[0, 4, 4, 0]} name="Quotations" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* ========== CUSTOMER INSIGHTS ========== */}
      {activeTab === 'customers' && customerInsights && (
        <div className="space-y-6" data-testid="customer-insights">
          {/* Top Customers */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="medical-card p-6" data-testid="top-customers">
              <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>Top Customers by Revenue</h3>
              {customerInsights.top_customers.length > 0 ? (
                <div className="space-y-3">
                  {customerInsights.top_customers.map((c, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 bg-[#F4F9F7] rounded-lg">
                      <span className="w-7 h-7 rounded-full bg-[#00a69c] text-white flex items-center justify-center text-xs font-bold">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[#052A23] truncate">{c.name || c.code}</span>
                          <span className="px-2 py-0.5 bg-[#E2F3F0] text-[#00a69c] text-xs rounded font-bold">{c.code}</span>
                        </div>
                        <span className="text-xs text-[#6D8E87]">{c.deals} deals</span>
                      </div>
                      <span className="text-sm font-bold text-[#00a69c] whitespace-nowrap">{formatCurrency(c.revenue)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#6D8E87]">No customer revenue data yet. Approve quotations to see rankings.</p>
              )}
            </div>

            {/* Customer Acquisition Trend */}
            <div className="medical-card p-6" data-testid="acquisition-trend">
              <h3 className="text-lg font-semibold text-[#052A23] mb-1" style={{ fontFamily: 'Outfit' }}>Customer Acquisition</h3>
              <p className="text-xs text-[#6D8E87] mb-4">New customers added per month</p>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={customerInsights.acquisition_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2F3F0" />
                  <XAxis dataKey="month" tickFormatter={formatMonth} stroke="#6D8E87" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#6D8E87" tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip labelFormatter={formatMonth} contentStyle={{ border: '1px solid #D5E8E4', borderRadius: '8px' }} />
                  <Bar dataKey="count" fill="#8B5CF6" radius={[4, 4, 0, 0]} name="New Customers" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Region + Customer Type */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Region-wise Revenue */}
            <div className="medical-card p-6" data-testid="region-revenue">
              <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>Region-wise Lead Value</h3>
              {customerInsights.region_data.length > 0 ? (
                <div className="space-y-3">
                  {customerInsights.region_data.map((r, i) => (
                    <div key={i}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-[#052A23]">{r.region}</span>
                        <div className="flex gap-3">
                          <span className="text-xs text-[#6D8E87]">{r.leads} leads</span>
                          <span className="text-sm font-bold text-[#00a69c]">{formatCurrency(r.revenue)}</span>
                        </div>
                      </div>
                      <div className="w-full bg-[#E2F3F0] rounded-full h-2">
                        <div className="h-2 rounded-full" style={{
                          width: `${Math.min((r.revenue / (customerInsights.region_data[0]?.revenue || 1)) * 100, 100)}%`,
                          backgroundColor: COLORS[i % COLORS.length]
                        }}></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-[#6D8E87]">No regional data yet.</p>
              )}
            </div>

            {/* Customer Type Breakdown */}
            <div className="medical-card p-6" data-testid="customer-type-chart">
              <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>Customer Type Breakdown</h3>
              {customerInsights.type_data.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={customerInsights.type_data} outerRadius={70} dataKey="count" nameKey="type" label={({ type, count }) => `${type}: ${count}`}>
                        {customerInsights.type_data.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v, n) => [v, n]} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap gap-3 mt-2 justify-center">
                    {customerInsights.type_data.map((t, i) => (
                      <span key={i} className="flex items-center gap-1 text-xs text-[#3B5A54]">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }}></span>
                        {t.type} ({formatCurrency(t.value)})
                      </span>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-sm text-[#6D8E87]">No customer type data yet.</p>
              )}
            </div>
          </div>

          {/* iter132.4 — Templates in play */}
          {tplUsage && (tplUsage.items?.length > 0 || (tplUsage.active_template_count || 0) > 0) && (
            <div className="medical-card p-5 mt-6" data-testid="templates-in-play-card">
              <div className="flex items-center justify-between gap-3 mb-2">
                <h3 className="text-lg font-semibold text-[#052A23] flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  Templates in play
                </h3>
                <div className="text-[11px] text-[#6D8E87]">
                  {tplUsage.active_template_count || 0} active · {tplUsage.total_reuses || 0} approval{tplUsage.total_reuses === 1 ? '' : 's'} bypassed
                </div>
              </div>
              <p className="text-xs text-[#6D8E87] mb-3">
                VP-approved payment terms combinations that repeat customers are actively reusing, saving approval rounds.
              </p>
              {tplUsage.items?.length > 0 ? (
                <ul className="divide-y divide-[#EAF3F0]" data-testid="templates-in-play-list">
                  {tplUsage.items.map((row) => (
                    <li key={row.template_id} className="py-2 flex items-start justify-between gap-3" data-testid={`templates-in-play-row-${row.template_id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#052A23] truncate">{row.customer_name || '—'}</div>
                        <div className="text-[11px] text-[#6D8E87] truncate">{row.summary}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-lg font-bold tabular-nums text-emerald-700">{row.reuse_count}</div>
                        <div className="text-[10px] uppercase tracking-wide text-[#6D8E87]">bypasses</div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-[#6D8E87] py-3" data-testid="templates-in-play-empty">
                  Templates are saved but haven't been reused yet.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ========== PRODUCT INSIGHTS ========== */}
      {activeTab === 'products' && productInsights && (
        <ProductInsightsPanel data={productInsights} formatCurrency={formatCurrency} />
      )}

      {/* ========== LOST ORDER ANALYSIS ========== */}
      {activeTab === 'lord' && lordAnalytics && (
        <div data-testid="lord-analytics">
          {/* LORD KPI Strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="medical-card p-4 border-l-4 border-red-400" data-testid="lord-kpi-total">
              <p className="text-xs text-[#6D8E87]">Total Lost Orders</p>
              <p className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Outfit' }}>{lordAnalytics.total_lost}</p>
            </div>
            <div className="medical-card p-4 border-l-4 border-red-400" data-testid="lord-kpi-value">
              <p className="text-xs text-[#6D8E87]">Total Lost Value</p>
              <p className="text-2xl font-bold text-red-600" style={{ fontFamily: 'Outfit' }}>{formatCurrency(lordAnalytics.total_lost_value)}</p>
            </div>
            <div className="medical-card p-4 border-l-4 border-amber-400" data-testid="lord-kpi-competitor">
              <p className="text-xs text-[#6D8E87]">Top Competitor</p>
              <p className="text-2xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>{lordAnalytics.competitor_data?.[0]?.name || '-'}</p>
            </div>
            <div className="medical-card p-4 border-l-4 border-blue-400" data-testid="lord-kpi-reason">
              <p className="text-xs text-[#6D8E87]">Top Loss Reason</p>
              <p className="text-2xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>{lordAnalytics.loss_reasons?.[0]?.reason || '-'}</p>
            </div>
          </div>

          {lordAnalytics.total_lost === 0 ? (
            <div className="medical-card p-12 text-center">
              <XCircle size={48} className="text-[#D5E8E4] mx-auto mb-3" />
              <h3 className="text-xl font-semibold text-[#052A23] mb-2">No Lost Order Data Yet</h3>
              <p className="text-[#6D8E87]">LORD analytics will appear once orders are marked as lost.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Competitors Chart */}
              <div className="medical-card p-5" data-testid="lord-competitors-chart">
                <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>Top Competitors</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={lordAnalytics.competitor_data} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#D5E8E4" />
                    <XAxis type="number" tick={{ fontSize: 12 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={100} />
                    <Tooltip formatter={(v, n) => n === 'value' ? formatCurrency(v) : v} />
                    <Bar dataKey="count" fill="#EF4444" radius={[0, 4, 4, 0]} name="Orders Lost" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Loss Reasons */}
              <div className="medical-card p-5" data-testid="lord-reasons-chart">
                <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>Loss Reasons</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={lordAnalytics.loss_reasons} dataKey="count" nameKey="reason" cx="50%" cy="50%" outerRadius={100} label={({ reason, percent }) => `${reason} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                      {lordAnalytics.loss_reasons.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v, n, p) => [`${v} orders (${formatCurrency(p.payload.value)})`, 'Lost']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Pricing Competitiveness */}
              <div className="medical-card p-5" data-testid="lord-pricing-chart">
                <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>Pricing Competitiveness</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={lordAnalytics.pricing_analysis}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#D5E8E4" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#F59E0B" radius={[4, 4, 0, 0]} name="Occurrences" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Relationship Strength */}
              <div className="medical-card p-5" data-testid="lord-relationship-chart">
                <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>Relationship Strength at Loss</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie data={lordAnalytics.relationship_data} dataKey="count" nameKey="strength" cx="50%" cy="50%" innerRadius={60} outerRadius={100} label={({ strength, count }) => `${strength}: ${count}`}>
                      {lordAnalytics.relationship_data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Improvement Areas */}
              <div className="medical-card p-5" data-testid="lord-improvement-chart">
                <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>Improvement Areas (Frequency)</h3>
                {lordAnalytics.improvement_areas.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={lordAnalytics.improvement_areas} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#D5E8E4" />
                      <XAxis type="number" tick={{ fontSize: 12 }} />
                      <YAxis dataKey="area" type="category" tick={{ fontSize: 11 }} width={130} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8B5CF6" radius={[0, 4, 4, 0]} name="Mentions" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-[#6D8E87]">No improvement area data yet.</p>
                )}
              </div>

              {/* Monthly Loss Trend */}
              <div className="medical-card p-5" data-testid="lord-trend-chart">
                <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>Monthly Loss Trend</h3>
                {lordAnalytics.monthly_trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <ComposedChart data={lordAnalytics.monthly_trend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#D5E8E4" />
                      <XAxis dataKey="month" tickFormatter={formatMonth} tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                      <YAxis yAxisId="right" orientation="right" tickFormatter={v => `₹${(v/1000).toFixed(0)}k`} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v, n) => n === 'value' ? formatCurrency(v) : v} labelFormatter={formatMonth} />
                      <Bar yAxisId="left" dataKey="count" fill="#EF4444" radius={[4, 4, 0, 0]} name="Lost Orders" />
                      <Line yAxisId="right" dataKey="value" stroke="#F59E0B" strokeWidth={2} dot={{ r: 4 }} name="Lost Value" />
                      <Legend />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-[#6D8E87]">No monthly data yet.</p>
                )}
              </div>

              {/* Avg Value by Loss Reason - Table */}
              <div className="medical-card p-5 lg:col-span-2" data-testid="lord-avg-value-table">
                <h3 className="text-lg font-semibold text-[#052A23] mb-4" style={{ fontFamily: 'Outfit' }}>Value Impact by Loss Reason</h3>
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[500px]">
                  <thead className="bg-[#F4F9F7]">
                    <tr>
                      <th className="text-left px-4 py-2.5 text-xs text-[#3B5A54] font-semibold">Loss Reason</th>
                      <th className="text-right px-4 py-2.5 text-xs text-[#3B5A54] font-semibold">Orders</th>
                      <th className="text-right px-4 py-2.5 text-xs text-[#3B5A54] font-semibold">Avg. Value</th>
                      <th className="text-right px-4 py-2.5 text-xs text-[#3B5A54] font-semibold">Total Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lordAnalytics.avg_value_by_reason.map((r) => (
                      <tr key={r.reason} className="border-t border-[#D5E8E4]">
                        <td className="px-4 py-2.5 font-medium text-[#052A23]">{r.reason}</td>
                        <td className="px-4 py-2.5 text-right text-[#6D8E87]">{lordAnalytics.loss_reasons?.find(lr => lr.reason === r.reason)?.count || '-'}</td>
                        <td className="px-4 py-2.5 text-right font-medium text-amber-600">{formatCurrency(r.avg_value)}</td>
                        <td className="px-4 py-2.5 text-right font-bold text-red-600">{formatCurrency(r.total_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Danger Zone removed in Feb 2026 — wipe-transactional-data endpoint deleted from backend. */}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────────
// Product Insights Panel
// Surfaces winners + laggards from won-lead approved quotations. GM slices
// are only populated for VP+/President — for everyone else the endpoint
// returns empty arrays and we hide those cards.
// ────────────────────────────────────────────────────────────────────────
const ProductInsightsPanel = ({ data, formatCurrency }) => {
  const empty = data.total_units_sold === 0;
  const fmtGm = (r) => r.gm_pct === null || r.gm_pct === undefined
    ? <span className="text-[10px] text-amber-700 italic">no COGS</span>
    : <span className={`font-semibold ${r.gm_pct >= 30 ? 'text-emerald-700' : r.gm_pct >= 15 ? 'text-amber-700' : 'text-red-600'}`}>{r.gm_pct.toFixed(1)}%</span>;

  const daysAgo = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return Math.max(0, Math.round((Date.now() - d.getTime()) / 86400000));
    } catch { return null; }
  };

  return (
    <div data-testid="product-insights">
      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="medical-card p-4 border-l-4 border-[#00a69c]" data-testid="pi-kpi-catalog">
          <p className="text-xs text-[#6D8E87]">Catalogue size</p>
          <p className="text-2xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>{data.catalog_size}</p>
        </div>
        <div className="medical-card p-4 border-l-4 border-emerald-400" data-testid="pi-kpi-sold">
          <p className="text-xs text-[#6D8E87]">Products with sales</p>
          <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'Outfit' }}>{data.products_with_sales}</p>
        </div>
        <div className="medical-card p-4 border-l-4 border-amber-400" data-testid="pi-kpi-never-sold">
          <p className="text-xs text-[#6D8E87]">Never sold</p>
          <p className="text-2xl font-bold text-amber-700" style={{ fontFamily: 'Outfit' }}>{data.never_sold_count}</p>
        </div>
        <div className="medical-card p-4 border-l-4 border-blue-400" data-testid="pi-kpi-units">
          <p className="text-xs text-[#6D8E87]">Total units sold</p>
          <p className="text-2xl font-bold text-[#052A23]" style={{ fontFamily: 'Outfit' }}>{data.total_units_sold.toLocaleString('en-IN')}</p>
        </div>
      </div>

      {empty ? (
        <div className="medical-card p-12 text-center">
          <Package size={48} className="text-[#D5E8E4] mx-auto mb-3" />
          <h3 className="text-xl font-semibold text-[#052A23] mb-2">No product sales yet</h3>
          <p className="text-[#6D8E87]">Product performance rankings will appear once won leads have approved quotations with line items.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Top 10 sellers (units) */}
          <div className="medical-card p-5" data-testid="pi-top-sellers">
            <h3 className="text-lg font-semibold text-[#052A23] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              🏆 Top 10 Sellers <span className="text-xs font-normal text-[#6D8E87]">by units</span>
              <CsvBtn
                rows={data.top_sellers}
                filename="top-sellers.csv"
                testId="pi-top-sellers-csv"
                columns={[
                  { key: 'product_name', label: 'Product' },
                  { key: 'units_sold', label: 'Units' },
                  { key: 'revenue', label: 'Revenue (INR)', format: (v) => (v ?? 0).toFixed(2) },
                  { key: 'quotes', label: 'Quotes' },
                  { key: 'last_sale_at', label: 'Last sale' },
                ]}
              />
            </h3>
            <ProductTable
              rows={data.top_sellers}
              cols={[
                { key: 'rank', label: '#', className: 'w-8 text-center text-[#6D8E87]', render: (_, i) => i + 1 },
                { key: 'product_name', label: 'Product', className: 'font-medium text-[#052A23]' },
                { key: 'trend', label: '12-mo trend', align: 'right', render: (r) => <Sparkline values={r.trend_units} months={data.trend_months} /> },
                { key: 'units_sold', label: 'Units', align: 'right', render: (r) => r.units_sold.toLocaleString('en-IN') },
                { key: 'revenue', label: 'Revenue', align: 'right', render: (r) => formatCurrency(r.revenue), className: 'font-semibold' },
              ]}
            />
          </div>

          {/* Top 10 by revenue */}
          <div className="medical-card p-5" data-testid="pi-top-revenue">
            <h3 className="text-lg font-semibold text-[#052A23] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              💰 Top 10 by Revenue
              <CsvBtn
                rows={data.top_revenue_products}
                filename="top-by-revenue.csv"
                testId="pi-top-revenue-csv"
                columns={[
                  { key: 'product_name', label: 'Product' },
                  { key: 'revenue', label: 'Revenue (INR)', format: (v) => (v ?? 0).toFixed(2) },
                  { key: 'units_sold', label: 'Units' },
                  { key: 'quotes', label: 'Quotes' },
                ]}
              />
            </h3>
            <ProductTable
              rows={data.top_revenue_products}
              cols={[
                { key: 'rank', label: '#', className: 'w-8 text-center text-[#6D8E87]', render: (_, i) => i + 1 },
                { key: 'product_name', label: 'Product', className: 'font-medium text-[#052A23]' },
                { key: 'trend', label: '12-mo trend', align: 'right', render: (r) => <Sparkline values={r.trend_units} months={data.trend_months} /> },
                { key: 'revenue', label: 'Revenue', align: 'right', render: (r) => formatCurrency(r.revenue), className: 'font-semibold text-emerald-700' },
                { key: 'units_sold', label: 'Units', align: 'right', render: (r) => r.units_sold.toLocaleString('en-IN') },
              ]}
            />
          </div>

          {/* Lowest performing (bottom 5 by units, with at least 1 sale) */}
          <div className="medical-card p-5" data-testid="pi-lowest-sellers">
            <h3 className="text-lg font-semibold text-[#052A23] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              📉 Lowest performing <span className="text-xs font-normal text-[#6D8E87]">(bottom 5 by units)</span>
              <CsvBtn
                rows={data.lowest_sellers}
                filename="lowest-performing-products.csv"
                testId="pi-lowest-sellers-csv"
                columns={[
                  { key: 'product_name', label: 'Product' },
                  { key: 'units_sold', label: 'Units' },
                  { key: 'revenue', label: 'Revenue (INR)', format: (v) => (v ?? 0).toFixed(2) },
                  { key: 'last_sale_at', label: 'Last sale' },
                ]}
              />
            </h3>
            {data.lowest_sellers.length === 0 ? (
              <p className="text-sm text-[#6D8E87]">Not enough data yet.</p>
            ) : (
              <ProductTable
                rows={data.lowest_sellers}
                cols={[
                  { key: 'product_name', label: 'Product', className: 'font-medium text-[#052A23]' },
                  { key: 'units_sold', label: 'Units', align: 'right', render: (r) => r.units_sold, className: 'text-red-600 font-semibold' },
                  { key: 'revenue', label: 'Revenue', align: 'right', render: (r) => formatCurrency(r.revenue) },
                  { key: 'last_sale', label: 'Last sale', align: 'right', render: (r) => {
                    const d = daysAgo(r.last_sale_at);
                    return d === null ? '—' : <span className={d > 90 ? 'text-red-600' : 'text-[#3B5A54]'}>{d}d ago</span>;
                  }},
                ]}
              />
            )}
          </div>

          {/* Never sold — newest 20 */}
          <div className="medical-card p-5" data-testid="pi-never-sold">
            <h3 className="text-lg font-semibold text-[#052A23] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              🚫 Never sold <span className="text-xs font-normal text-[#6D8E87]">(newest 20)</span>
              <CsvBtn
                rows={data.never_sold}
                filename="never-sold-products.csv"
                testId="pi-never-sold-csv"
                columns={[
                  { key: 'product_code', label: 'Code' },
                  { key: 'product_name', label: 'Product' },
                  { key: 'list_price', label: 'List price (INR)', format: (v) => (v ?? 0).toFixed(2) },
                  { key: 'created_at', label: 'Created' },
                ]}
              />
            </h3>
            {data.never_sold.length === 0 ? (
              <p className="text-sm text-[#6D8E87]">Every product has had at least one sale — nice going!</p>
            ) : (
              <ProductTable
                rows={data.never_sold}
                cols={[
                  { key: 'product_code', label: 'Code', className: 'font-mono text-xs text-[#6D8E87]', render: (r) => r.product_code || '—' },
                  { key: 'product_name', label: 'Product', className: 'font-medium text-[#052A23]' },
                  { key: 'list_price', label: 'List price', align: 'right', render: (r) => formatCurrency(r.list_price) },
                  { key: 'age', label: 'Age', align: 'right', render: (r) => {
                    const d = daysAgo(r.created_at);
                    return d === null ? '—' : `${d}d`;
                  }},
                ]}
              />
            )}
          </div>

          {/* iter127 — Category roll-up (leadership's "which family is growing?" lens) */}
          {data.categories && data.categories.length > 0 && (
            <div className="medical-card p-5 lg:col-span-2" data-testid="pi-categories">
              <h3 className="text-lg font-semibold text-[#052A23] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                🗂️ Category Roll-up <span className="text-xs font-normal text-[#6D8E87]">revenue by product family</span>
                <CsvBtn
                  rows={data.categories}
                  filename="product-categories.csv"
                  testId="pi-categories-csv"
                  columns={[
                    { key: 'category', label: 'Category' },
                    { key: 'product_count', label: 'SKUs sold' },
                    { key: 'units_sold', label: 'Units' },
                    { key: 'revenue', label: 'Revenue (INR)', format: (v) => (v ?? 0).toFixed(2) },
                    { key: 'revenue_share_pct', label: 'Share %', format: (v) => (v ?? 0).toFixed(2) },
                    { key: 'gm_pct', label: 'GM %', format: (v) => v === null || v === undefined ? '' : v.toFixed(2) },
                  ]}
                />
              </h3>
              <ProductTable
                rows={data.categories}
                cols={[
                  { key: 'rank', label: '#', className: 'w-8 text-center text-[#6D8E87]', render: (_, i) => i + 1 },
                  { key: 'category', label: 'Category', className: 'font-medium text-[#052A23]' },
                  { key: 'product_count', label: 'SKUs', align: 'right' },
                  { key: 'trend', label: '12-mo trend', align: 'right', render: (r) => <Sparkline values={r.trend_units} months={data.trend_months} /> },
                  { key: 'units_sold', label: 'Units', align: 'right', render: (r) => r.units_sold.toLocaleString('en-IN') },
                  { key: 'revenue', label: 'Revenue', align: 'right', render: (r) => formatCurrency(r.revenue), className: 'font-semibold text-emerald-700' },
                  { key: 'revenue_share_pct', label: 'Share', align: 'right', render: (r) => (
                    <div className="inline-flex items-center gap-1.5">
                      <div className="w-14 h-1.5 bg-[#F0F5F3] rounded-full overflow-hidden">
                        <div className="h-full bg-[#00a69c]" style={{ width: `${Math.min(100, r.revenue_share_pct)}%` }} />
                      </div>
                      <span className="text-xs font-mono">{r.revenue_share_pct.toFixed(1)}%</span>
                    </div>
                  ) },
                  ...(data.gm_available ? [{
                    key: 'gm_pct', label: 'Avg GM', align: 'right', render: (r) => r.gm_pct === null || r.gm_pct === undefined
                      ? <span className="text-[10px] text-amber-700 italic">no COGS</span>
                      : <span className={`font-semibold ${r.gm_pct >= 30 ? 'text-emerald-700' : r.gm_pct >= 15 ? 'text-amber-700' : 'text-red-600'}`}>{r.gm_pct.toFixed(1)}%</span>
                  }] : []),
                ]}
              />
            </div>
          )}

          {/* GM slices — VP+/President only */}
          {data.gm_available && (
            <>
              <div className="medical-card p-5" data-testid="pi-top-gm">
                <h3 className="text-lg font-semibold text-[#052A23] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  📈 Top GM% <span className="text-xs font-normal text-[#6D8E87]">top 10 by margin</span>
                  <CsvBtn
                    rows={data.top_gm_products}
                    filename="top-gm-pct-products.csv"
                    testId="pi-top-gm-csv"
                    columns={[
                      { key: 'product_name', label: 'Product' },
                      { key: 'gm_pct', label: 'GM %', format: (v) => (v ?? 0).toFixed(2) },
                      { key: 'gm_amount', label: 'GM (INR)', format: (v) => (v ?? 0).toFixed(2) },
                      { key: 'revenue', label: 'Revenue (INR)', format: (v) => (v ?? 0).toFixed(2) },
                      { key: 'units_sold', label: 'Units' },
                    ]}
                  />
                </h3>
                {data.top_gm_products.length === 0 ? (
                  <p className="text-sm text-[#6D8E87]">No products with COGS on file yet.</p>
                ) : (
                  <ProductTable
                    rows={data.top_gm_products}
                    cols={[
                      { key: 'rank', label: '#', className: 'w-8 text-center text-[#6D8E87]', render: (_, i) => i + 1 },
                      { key: 'product_name', label: 'Product', className: 'font-medium text-[#052A23]' },
                      { key: 'gm_pct', label: 'GM%', align: 'right', render: fmtGm },
                      { key: 'revenue', label: 'Revenue', align: 'right', render: (r) => formatCurrency(r.revenue) },
                    ]}
                  />
                )}
              </div>

              <div className="medical-card p-5" data-testid="pi-bottom-gm">
                <h3 className="text-lg font-semibold text-[#052A23] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  📉 Lowest GM% <span className="text-xs font-normal text-[#6D8E87]">bottom 5 — pricing / cost review candidates</span>
                  <CsvBtn
                    rows={data.bottom_gm_products}
                    filename="lowest-gm-pct-products.csv"
                    testId="pi-bottom-gm-csv"
                    columns={[
                      { key: 'product_name', label: 'Product' },
                      { key: 'gm_pct', label: 'GM %', format: (v) => (v ?? 0).toFixed(2) },
                      { key: 'revenue', label: 'Revenue (INR)', format: (v) => (v ?? 0).toFixed(2) },
                      { key: 'units_sold', label: 'Units' },
                    ]}
                  />
                </h3>
                {data.bottom_gm_products.length === 0 ? (
                  <p className="text-sm text-[#6D8E87]">Awaiting COGS data.</p>
                ) : (
                  <ProductTable
                    rows={data.bottom_gm_products}
                    cols={[
                      { key: 'product_name', label: 'Product', className: 'font-medium text-[#052A23]' },
                      { key: 'gm_pct', label: 'GM%', align: 'right', render: fmtGm },
                      { key: 'revenue', label: 'Revenue', align: 'right', render: (r) => formatCurrency(r.revenue) },
                      { key: 'units_sold', label: 'Units', align: 'right' },
                    ]}
                  />
                )}
              </div>

              <div className="medical-card p-5 lg:col-span-2" data-testid="pi-top-gm-amount">
                <h3 className="text-lg font-semibold text-[#052A23] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                  💎 Top GM Contributors <span className="text-xs font-normal text-[#6D8E87]">by absolute margin ₹</span>
                  <CsvBtn
                    rows={data.top_gm_amount_products}
                    filename="top-gm-contributors.csv"
                    testId="pi-top-gm-amount-csv"
                    columns={[
                      { key: 'product_name', label: 'Product' },
                      { key: 'gm_amount', label: 'GM (INR)', format: (v) => (v ?? 0).toFixed(2) },
                      { key: 'gm_pct', label: 'GM %', format: (v) => (v ?? 0).toFixed(2) },
                      { key: 'revenue', label: 'Revenue (INR)', format: (v) => (v ?? 0).toFixed(2) },
                      { key: 'units_sold', label: 'Units' },
                    ]}
                  />
                </h3>
                {data.top_gm_amount_products.length === 0 ? (
                  <p className="text-sm text-[#6D8E87]">Awaiting COGS data.</p>
                ) : (
                  <ProductTable
                    rows={data.top_gm_amount_products}
                    cols={[
                      { key: 'rank', label: '#', className: 'w-8 text-center text-[#6D8E87]', render: (_, i) => i + 1 },
                      { key: 'product_name', label: 'Product', className: 'font-medium text-[#052A23]' },
                      { key: 'gm_amount', label: 'GM ₹', align: 'right', render: (r) => <span className="font-semibold text-emerald-700">{formatCurrency(r.gm_amount || 0)}</span> },
                      { key: 'gm_pct', label: 'GM%', align: 'right', render: fmtGm },
                      { key: 'revenue', label: 'Revenue', align: 'right', render: (r) => formatCurrency(r.revenue) },
                      { key: 'units_sold', label: 'Units', align: 'right' },
                    ]}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// Tiny inline sparkline — pure SVG so it's tiny, doesn't allocate a
// recharts container per row, and animates crisply. Renders a filled
// area + polyline over 12 values. `months` is used only for the tooltip.
const Sparkline = ({ values = [], months = [], width = 96, height = 28 }) => {
  if (!values || values.length === 0) {
    return <span className="text-[10px] text-[#94A3B8] italic">—</span>;
  }
  const total = values.reduce((s, v) => s + (v || 0), 0);
  if (total === 0) {
    return <span className="text-[10px] text-[#94A3B8] italic">no data</span>;
  }
  const max = Math.max(...values, 1);
  const step = width / Math.max(1, values.length - 1);
  const points = values.map((v, i) => {
    const x = i * step;
    // 2px top-bottom padding so peaks/troughs don't touch the edge
    const y = height - 2 - ((v || 0) / max) * (height - 4);
    return [x, y];
  });
  const pathD = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`).join(' ');
  const areaD = `${pathD} L${width} ${height} L0 ${height} Z`;
  // Momentum tint: last-3 vs first-3 average — teal if trending up, amber flat, red down.
  const first3 = values.slice(0, 3);
  const last3 = values.slice(-3);
  const avg = (arr) => (arr.reduce((s, v) => s + (v || 0), 0) / (arr.length || 1));
  const delta = avg(last3) - avg(first3);
  const strokeColor = delta > 0.1 ? '#00a69c' : delta < -0.1 ? '#dc2626' : '#f59e0b';
  const fillColor = delta > 0.1 ? 'rgba(0,166,156,0.16)' : delta < -0.1 ? 'rgba(220,38,38,0.16)' : 'rgba(245,158,11,0.16)';
  const monthLabel = (mk) => {
    if (!mk) return '';
    const [y, m] = mk.split('-');
    const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${names[parseInt(m, 10) - 1] || m} ${y?.slice(2) || ''}`;
  };
  const tooltipTitle = values
    .map((v, i) => `${monthLabel(months[i])}: ${v.toLocaleString('en-IN')}`)
    .join('\n');
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="inline-block align-middle" role="img" aria-label="12-month trend">
      <title>{tooltipTitle}</title>
      <path d={areaD} fill={fillColor} />
      <path d={pathD} fill="none" stroke={strokeColor} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {points.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={i === points.length - 1 ? 2 : 0} fill={strokeColor} />
      ))}
    </svg>
  );
};

// Tiny table helper used by the Product Insights cards.
const ProductTable = ({ rows, cols }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead className="bg-[#F4F9F7]">
        <tr>
          {cols.map((c) => (
            <th key={c.key} className={`px-3 py-2 text-xs text-[#3B5A54] font-semibold ${c.align === 'right' ? 'text-right' : 'text-left'}`}>{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={r.product_id || r.product_code || i} className="border-t border-[#E2ECEA] hover:bg-[#F9FCFB]">
            {cols.map((c) => (
              <td key={c.key} className={`px-3 py-2 ${c.align === 'right' ? 'text-right' : ''} ${c.className || ''}`}>
                {c.render ? c.render(r, i) : r[c.key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// Small right-aligned "Export CSV" button that sits beside a card title.
const CsvBtn = ({ rows, columns, filename, testId }) => (
  <button
    type="button"
    onClick={() => exportRowsToCsv({ filename, columns, rows })}
    disabled={!rows || rows.length === 0}
    className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-semibold bg-[#F4F9F7] text-[#00857e] border border-[#D5E8E4] hover:bg-[#E2F3F0] disabled:opacity-40"
    title={`Export ${rows?.length || 0} rows to CSV`}
    data-testid={testId}
  >
    <DownloadSimple size={12} weight="bold" /> CSV
  </button>
);

export default Dashboard;
